import type { PostHogConfig } from "posthog-js";
import { register, setPersonProperties, startAnalytics } from "@/lib/analytics-client";
import { shouldDropException } from "@/lib/exception-noise";

const SUPPORTED_LOCALES = ["ko", "ja", "id", "zh", "th", "tl", "ms", "vi", "es", "pt"] as const;

function detectLocaleFromPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (first && (SUPPORTED_LOCALES as readonly string[]).includes(first)) {
    return first;
  }
  return "en";
}

function registerLocaleProps() {
  if (typeof window === "undefined") return;
  const locale = detectLocaleFromPath(window.location.pathname);
  // $locale becomes a property on every subsequent event until updated again.
  // We register it on init AND on every history change so SPA navigation between
  // locale and English pages updates the dimension correctly.
  register({ $locale: locale });
  // Set as person property too (sticky across sessions for identified users) so
  // we can segment by primary-language user, not just per-event locale.
  setPersonProperties({ last_locale: locale });
}

const token = process.env.NEXT_PUBLIC_POSTHOG_TOKEN;

if (token) {
  const options: Partial<PostHogConfig> = {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    defaults: "2025-05-24",
    // Load PostHog's lazy modules into <head>, not <body>.
    //
    // The SDK injects array.js, exception-autocapture, dead-clicks-autocapture
    // and web-vitals as separate <script> elements. On the "body" target it
    // inserts them before the first `body > script` — which on this site is a
    // JSON-LD block, since 65 components render schema inline. When that
    // injection wins the race against hydration, React reaches for its
    // <script type="application/ld+json"> and finds type="text/javascript"
    // with a posthog src instead: an attribute mismatch, surfacing as minified
    // React error #418. Reproduced 2026-08-18 on /technique/alternate-picking;
    // the dev-mode diff named exception-autocapture.js as the intruder.
    //
    // PostHog already made "head" the default for `defaults >= 2026-01-30`.
    // Setting it explicitly rather than bumping `defaults` keeps the blast
    // radius to this one behaviour instead of every flag in that cohort.
    external_scripts_inject_target: "head",
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    capture_pageleave: true,
    // Capture unhandled JS errors and promise rejections so we have real-user
    // signal on hydration mismatches, undefined refs, etc.
    capture_exceptions: true,
    // Drop exceptions that can never be acted on — visitor browser extensions,
    // aborted fetches, and anything from a developer's localhost. See
    // exception-noise.ts for what each rule exists to catch.
    before_send: (event) => {
      const hostname = typeof window === "undefined" ? "" : window.location.hostname;
      return shouldDropException(event, hostname) ? null : event;
    },
    // Capture Core Web Vitals from real users. This captures INP, LCP, CLS and
    // FCP and NOT TTFB -- posthog-js cannot collect TTFB at all, its
    // SupportedWebVitalsMetrics type is exactly 'LCP' | 'CLS' | 'FCP' | 'INP'.
    // A previous version of this comment listed TTFB, and a reader believed it
    // and went looking for server timing that was never being recorded.
    // TTFB comes from Vercel Speed Insights instead (see app/layout.tsx).
    //
    // Treat the per-page attribution here with care: these metrics are reported
    // once per document by the web-vitals library, which knows nothing about
    // client-side navigation, and the URL is attached when the metric is SENT.
    // On routes users usually reach by navigating within the app rather than
    // landing on directly -- /app above all -- the path on the event is not
    // reliably the path that was measured.
    capture_performance: {
      network_timing: true,
      web_vitals: true,
    },
    // Replay sessions for rage-click / paywall-drop / cancel-intercept
    // investigations. Mask inputs by default so we never record passwords,
    // emails, or audio uploads. Sampling lives in the PostHog project settings.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-private], input[type='password'], input[type='email']",
    },
    enable_heatmaps: true,
    // Surveys ship as their own ~100KB module that PostHog downloads on every
    // page load. Checked 2026-09-07 against the exact endpoint the browser SDK
    // calls (/api/surveys/?token=...): it returns zero surveys, so the module
    // was being fetched to display nothing.
    //
    // Surveys are authored in the PostHog UI, not in this repo -- so if you
    // ever create one there, it will NOT appear until this line is removed.
    disable_surveys: true,
    // Persist UTM + referrer across navigation so $initial_* attribution is
    // captured for FB / Reddit landings that bounce, refresh, or oauth-roundtrip.
    persistence: "localStorage+cookie",
    cross_subdomain_cookie: false,
    loaded: () => {
      registerLocaleProps();
    },
  };

  // Load the SDK once the browser is no longer busy with the page itself.
  //
  // This file is Next's client instrumentation entry, so it runs before
  // everything else -- which meant analytics was fetched and parsed ahead of
  // the product, and PostHog's five injected scripts started competing for the
  // network at 1,796ms. An idle callback yields to hydration; the 2s timeout is
  // the backstop so a permanently busy main thread cannot postpone analytics
  // indefinitely. Events fired in the meantime are buffered, not dropped.
  const begin = () => void startAnalytics(token, options);
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(begin, { timeout: 2000 });
  } else {
    setTimeout(begin, 1200);
  }

  if (typeof window !== "undefined") {
    // Re-register locale on SPA navigation. PostHog auto-captures the pageview
    // via capture_pageview: "history_change"; we update $locale just before so
    // the captured pageview event has the correct locale dimension.
    const updateLocale = () => registerLocaleProps();
    window.addEventListener("popstate", updateLocale);
    const origPush = history.pushState;
    history.pushState = function (...args) {
      const result = origPush.apply(this, args);
      updateLocale();
      return result;
    };
    const origReplace = history.replaceState;
    history.replaceState = function (...args) {
      const result = origReplace.apply(this, args);
      updateLocale();
      return result;
    };
  }
}
